import {
    ChangeDetectionStrategy,
    Component,
    Input,
    Self,
    OnInit,
    ChangeDetectorRef,
} from '@angular/core';
import { AbstractControl, FormControl, NgControl, ValidatorFn } from '@angular/forms';
import { filter, map, tap } from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { EntityFormControl } from '@bazis/form/models/form.types';
import { BazisFormService } from '@bazis/form/services/form.service';
import { combineLatest, merge, Observable, of, shareReplay, switchMap } from 'rxjs';
import { BazisConfigurationService, SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { BazisSrvService } from '@bazis/shared/services/srv.service';
import { BazisInputHintComponent } from '@bazis/form/components/base/hint/input-hint.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-tin-organization',
    templateUrl: './input-tin-organization.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BazisInputTinOrganizationComponent extends BazisInputHintComponent implements OnInit {
    @Input()
    tinField = 'tin';

    @Input()
    organizationField = 'organization';

    @Input()
    placeholderListKey: string;

    @Input()
    allowDirectInput: boolean = false; // true if the user is allowed to enter company name without selection organization from the list

    @Input() orgType$: Observable<'ip' | 'ul' | 'any'> = of('any');

    searchValue$ = this.searchControl.valueChanges.pipe(
        filter((v) => v !== undefined && v !== this.valueObject.searchValue),
        tap((value) => {
            const orgFieldValue = this.allowDirectInput ? this.searchControl.value : null;
            this.valueObject = {
                [this.organizationField]: orgFieldValue,
                [this.tinField]: null,
                searchValue: null,
            };
            this.searchControl.updateValueAndValidity({ emitEvent: false });
            this.onChange({ [this.organizationField]: orgFieldValue, [this.tinField]: null });
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    listFromAddress$;

    list$;

    reqField;

    valueObject;

    constructor(
        @Self() public ngControl: NgControl,
        protected cdr: ChangeDetectorRef,
        public configurationService: BazisConfigurationService,
        protected entityService: BazisEntityService,
        protected srv: BazisSrvService,
    ) {
        super(ngControl, cdr, configurationService, entityService);
    }

    ngOnInit() {
        if (this.ngControl.control instanceof EntityFormControl) {
            BazisFormService.getPropertiesFromConfig(this, this.ngControl.control.$config);

            this.reqField = (this.ngControl.control as EntityFormControl).$config?.fieldConfig[
                this.organizationField
            ].required._
                ? this.organizationField
                : this.tinField;

            this.required$ = (this.ngControl.control as EntityFormControl).$config?.fieldConfig[
                this.reqField
            ].required.$;

            this.searchControl.setValidators([this.validateControl(this.ngControl.control)]);

            this.ngControl.control.$config.componentValidatorFns = [this.validate.bind(this)];
            this.ngControl.control.addValidators(this.validate.bind(this));
        }

        this._isInited = true;
        this.writeValue(this.ngControl.control.value);

        if (
            (this.ngControl.control as EntityFormControl).$config?.fieldConfig[this.tinField]
                .readonly
        ) {
            this.setDisabledState(true);
        } else {
            this.setDisabledState(false);
        }

        this.ngControl.control.updateValueAndValidity();

        this.initList();
    }

    validateControl(originalControl: AbstractControl): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            const isRequired = (originalControl as EntityFormControl).$config?.fieldConfig[
                this.reqField
            ].required._;

            if (!isRequired && !control.value && !this.valueObject[this.reqField]) return null;

            if (isRequired && control.value && this.valueObject[this.reqField]) return null;

            if (control.value && !this.valueObject[this.reqField] && !this.allowDirectInput)
                return { selectOrganizationFromList: true };

            if (isRequired && (!control.value || !this.valueObject[this.reqField]))
                return { required: true };

            return null;
        };
    }

    validate({ value }: FormControl) {
        if (this.searchControl.status !== 'INVALID') {
            return null;
        }
        return {
            componentError: { ...this.searchControl.errors },
        };
    }

    public writeValue(value): void {
        if (!this._isInited) return;

        let searchValue = null;

        if (!!value && (value[this.tinField] || value[this.organizationField])) {
            searchValue =
                (value[this.tinField] || '') + ' ' + (value[this.organizationField] || '');
        }

        this.valueObject = {
            ...value,
            searchValue,
        };

        this.searchControl.setValue(searchValue, { emitEvent: false });
    }

    selectValue(value: any) {
        const searchValue = value.inn + ' ' + value.name;
        const nextValue = {
            [this.tinField]: value.inn,
            [this.organizationField]: value.name,
            searchValue,
        };

        this.valueObject = { ...nextValue };

        this.searchControl.setValue(searchValue);

        this.onChange({ ...nextValue });
        this.hideList();
    }

    initList() {
        this.listFromAddress$ = combineLatest([this.searchValue$, this.orgType$]).pipe(
            switchMap(([value, orgType]) => {
                const filterOrgType = orgType === 'any' ? {} : { org_type: orgType };
                return this.searchControl.value
                    ? this.srv.commonGetRequest$('dadata/suggest_organization', {
                          data: value,
                          ...filterOrgType,
                      })
                    : of([]);
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );

        this.list$ = merge(this.listFromAddress$).pipe(
            map((list: any[]) =>
                list.map((item) => {
                    return {
                        ...item.data,
                        name: item.value,
                    };
                }),
            ),
            tap((v) => {
                this.showList = true;
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }
}
